import { useCallback, useEffect, useState, type ReactElement } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useStamina, STAMINA_MAX } from '../hooks/useStamina'
import { useDaily } from '../hooks/useDaily'
import { useLiveMatchSheet } from '../components/useLiveMatchSheet'
import { track } from '../analytics'
import { apiClient } from '../api/client'
import { HeroCard, type HeroState } from '../components/lobby/HeroCard'
import { CtaCard } from '../components/lobby/CtaCard'
import { StaminaCard } from '../components/lobby/StaminaCard'
import { StreakCard } from '../components/lobby/StreakCard'
import { Top3Row } from '../components/lobby/Top3Row'
import { TriGrid } from '../components/lobby/TriGrid'
import { Footer } from '../components/lobby/Footer'
import { EmptyState } from '../components/EmptyState'
import { AppleChar } from '../components/AppleChar'
import { Icon } from '../components/Icon'
import type { LeaderboardEntry } from '@sagwa/types'

// ── Helpers ──────────────────────────────────────────────────────────────────

function todayKstDateString(): string {
  const now = new Date()
  const kst = new Date(now.getTime() + 9 * 60 * 60 * 1000)
  return kst.toISOString().slice(0, 10)
}

/** Seconds until the next UTC midnight. */
function secondsUntilUtcMidnight(): number {
  const now = new Date()
  const midnight = new Date(
    Date.UTC(
      now.getUTCFullYear(),
      now.getUTCMonth(),
      now.getUTCDate() + 1,
    ),
  )
  return Math.max(0, Math.floor((midnight.getTime() - now.getTime()) / 1000))
}

function formatHms(secs: number): string {
  const h = Math.floor(secs / 3600)
  const m = Math.floor((secs % 3600) / 60)
  const s = secs % 60
  return [h, m, s].map((v) => String(v).padStart(2, '0')).join(':')
}

// ── Component ─────────────────────────────────────────────────────────────────

export default function Lobby(): ReactElement {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const stamina = useStamina()
  const daily = useDaily()
  const liveMatch = useLiveMatchSheet()

  const [firstName, setFirstName] = useState<string>('')
  const [top3, setTop3] = useState<LeaderboardEntry[]>([])
  const [midnightSecs, setMidnightSecs] = useState(secondsUntilUtcMidnight)
  // Telegram WebApp user.photo_url is the only reliable client-side source
  // for the user's avatar — the API does not persist Telegram photo URLs
  // (they rotate). Read once on mount; the URL is stable for the session.
  const photoUrl =
    typeof window !== 'undefined'
      ? window.Telegram?.WebApp?.initDataUnsafe?.user?.photo_url
      : undefined

  // Pageview tracking
  useEffect(() => {
    track('lobby_view')
  }, [])

  // Fetch user firstName (useStamina fetches /me but does not expose user)
  useEffect(() => {
    void apiClient.getMe().then((me) => {
      setFirstName(me.user.firstName)
    })
  }, [])

  // Fetch top-3 daily leaderboard
  const fetchTop3 = useCallback(async (): Promise<void> => {
    try {
      const resp = await apiClient.getDailyLeaderboard(todayKstDateString())
      setTop3(resp.entries.slice(0, 3))
    } catch {
      // Non-critical — show empty state on failure
    }
  }, [])

  useEffect(() => {
    void fetchTop3()
  }, [fetchTop3])

  // Countdown to UTC midnight (ticks every second)
  useEffect(() => {
    const id = window.setInterval(() => {
      setMidnightSecs(secondsUntilUtcMidnight())
    }, 1000)
    return () => window.clearInterval(id)
  }, [])

  // ── Derived state ────────────────────────────────────────────────────────

  const staminaCurrent = stamina.stamina?.current ?? 0
  const dailyPlayed = (daily.today?.attemptCount ?? 0) > 0
  const isDailyBest =
    daily.today?.yourBest != null &&
    daily.today.yourBest > 0 &&
    stamina.todayBest != null &&
    daily.today.yourBest === stamina.todayBest

  const heroState: HeroState = (() => {
    if (staminaCurrent === 0) return 'lowStamina'
    if (!dailyPlayed) return 'unplayed'
    if (isDailyBest) return 'record'
    return 'played'
  })()

  const dailyScore = daily.today?.yourBest ?? undefined
  // rank is not in DailyTodayResponse — use null (not yet available at top level)
  const dailyRank = undefined

  const daily0Stamina = staminaCurrent === 0

  return (
    <div className="lobby-body">
      <HeroCard
        state={heroState}
        score={dailyScore}
        rank={dailyRank}
        regenSeconds={stamina.countdownToNextRegen}
        firstName={firstName || '…'}
        photoUrl={photoUrl}
        onStart={() => navigate('/daily')}
        onShare={() => {
          // Re-open today's results so the user can use the full share
          // flow (prepareShare + openShareDialog). Pre-fill state from
          // useDaily; rank is re-fetched inside DailyResults via its
          // caption effect.
          if (dailyScore != null) {
            navigate('/daily/results', {
              state: {
                serverScore: dailyScore,
                date: todayKstDateString(),
                rank: dailyRank ?? null,
                newDailyBest: false,
                newWeeklyBest: false,
              },
            })
          }
        }}
      />

      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        <CtaCard
          icon={<AppleChar size={32} mood="happy" />}
          title={t('lobby.daily.title')}
          sub={t('lobby.daily.sub', { time: formatHms(midnightSecs) })}
          disabled={daily0Stamina}
          onClick={daily0Stamina ? undefined : () => navigate('/daily')}
        />
        <CtaCard
          icon={<Icon name="swords" size={22} color="var(--apple-red)" />}
          title={t('lobby.match.title')}
          sub={t('lobby.match.subSoon')}
          onClick={() => liveMatch.open()}
        />
      </div>

      <div className="duo-row">
        <StaminaCard
          current={staminaCurrent}
          max={STAMINA_MAX}
          regenSeconds={stamina.countdownToNextRegen}
        />
        <StreakCard count={null} />
      </div>

      <div className="top3">
        <div className="sec-head">{t('lobby.top3.title')}</div>
        {top3.length === 0 ? (
          <EmptyState
            mood="sleepy"
            title={t('lobby.top3.emptyTitle')}
            subtitle={t('lobby.top3.emptySub')}
          />
        ) : (
          top3.map((entry, i) => (
            <Top3Row
              key={entry.userId}
              rank={(i + 1) as 1 | 2 | 3}
              name={entry.displayName}
              score={entry.score}
              isMe={entry.isYou ?? false}
            />
          ))
        )}
      </div>

      <TriGrid
        onFreePlay={() => navigate('/freeplay')}
        onLeaderboards={() => navigate('/leaderboards')}
        onHistory={() => navigate('/match/history')}
      />

      <Footer />
    </div>
  )
}
